export interface ChangelogSection {
  title: string;
  items: string[];
}

export interface ChangelogVersion {
  version: string;
  date: string;
  sections: ChangelogSection[];
}

/** Parses a structured CHANGELOG.md string into typed version entries. */
export function parseChangelog(markdown: string): ChangelogVersion[] {
  const versions: ChangelogVersion[] = [];
  // Split on version headings (## x.y.z)
  const versionBlocks = markdown.split(/^## /m).slice(1);

  for (const block of versionBlocks) {
    const lines = block.split('\n');
    const version = lines[0].trim();

    let date = '';
    const sections: ChangelogSection[] = [];
    let currentSection: ChangelogSection | null = null;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];

      const dateMatch = line.match(/^\*\*Date:\*\*\s*(.+)/);
      if (dateMatch) {
        date = dateMatch[1].trim();
        continue;
      }

      if (line.startsWith('### ')) {
        currentSection = { title: line.slice(4).trim(), items: [] };
        sections.push(currentSection);
        continue;
      }

      const itemMatch = line.match(/^-\s+(.+)/);
      if (itemMatch && currentSection) {
        let itemContent = itemMatch[1].trim();

        // Look ahead for sub-items (indented lines starting with "- ")
        while (i + 1 < lines.length) {
          const nextLine = lines[i + 1];
          // Stop if we hit a new section, version, date, or parent-level item
          if (nextLine.startsWith('### ') ||
              nextLine.startsWith('## ') ||
              nextLine.match(/^-\s+/) ||
              nextLine.match(/^\*\*Date:/)) {
            break;
          }
          // Check if it's a sub-item (2+ spaces followed by "- ")
          const subItemMatch = nextLine.match(/^\s{2,}-\s+(.+)/);
          if (subItemMatch) {
            itemContent += '\n' + nextLine;
            i++;
          } else {
            break;
          }
        }

        currentSection.items.push(itemContent);
      }
    }

    if (version) versions.push({ version, date, sections });
  }

  return versions;
}

/** Compares two semver-like version strings numerically. Returns positive if a > b. */
export function compareVersions(a: string, b: string): number {
  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/** Fetches and parses the changelog from /CHANGELOG.md. Returns null on failure. */
export async function fetchChangelog(): Promise<ChangelogVersion[] | null> {
  try {
    const res = await fetch('/CHANGELOG.md');
    if (!res.ok) return null;
    const text = await res.text();
    return parseChangelog(text);
  } catch (err) {
    console.error('Failed to fetch changelog:', err);
    return null;
  }
}
